import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HeartHandshake, HelpCircle, X, icons } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUserSettings, HelpAlertType } from "@/lib/userSettings";
import { useElementOverrides } from "@/contexts/ElementOverrideContext";
import { speak, stopSpeaking } from "@/lib/ttsService";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/hooks/useTranslation";
import { MainLayout } from "@/components/layout/MainLayout";

// Generate alert sounds using Web Audio API
function createAlertSound(type: HelpAlertType, audioContext: AudioContext): () => void {
  const gainNode = audioContext.createGain();
  gainNode.connect(audioContext.destination);
  gainNode.gain.value = 0.5;

  switch (type) {
    case "siren": {
      const oscillator = audioContext.createOscillator();
      oscillator.type = "sawtooth";
      oscillator.connect(gainNode);
      
      const startTime = audioContext.currentTime;
      const duration = 2;
      
      for (let i = 0; i < 4; i++) {
        oscillator.frequency.setValueAtTime(800, startTime + i * 0.5);
        oscillator.frequency.linearRampToValueAtTime(1200, startTime + i * 0.5 + 0.25);
        oscillator.frequency.linearRampToValueAtTime(800, startTime + i * 0.5 + 0.5);
      }
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
      
      return () => {
        try { oscillator.stop(); } catch {}
      };
    }
    
    case "beep": {
      const playBeep = (delay: number) => {
        const osc = audioContext.createOscillator();
        const beepGain = audioContext.createGain();
        osc.connect(beepGain);
        beepGain.connect(audioContext.destination);
        osc.type = "square";
        osc.frequency.value = 1000;
        beepGain.gain.value = 0.4;
        
        const startTime = audioContext.currentTime + delay;
        osc.start(startTime);
        osc.stop(startTime + 0.15);
        beepGain.gain.setValueAtTime(0.4, startTime + 0.12);
        beepGain.gain.linearRampToValueAtTime(0, startTime + 0.15);
      };
      
      playBeep(0);
      playBeep(0.25);
      playBeep(0.5);
      
      return () => {};
    }
    
    case "train": {
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const trainGain = audioContext.createGain();
      
      oscillator1.connect(trainGain);
      oscillator2.connect(trainGain);
      trainGain.connect(audioContext.destination);
      
      oscillator1.type = "sawtooth";
      oscillator2.type = "sawtooth";
      oscillator1.frequency.value = 440;
      oscillator2.frequency.value = 554;
      
      trainGain.gain.value = 0;
      
      const startTime = audioContext.currentTime;
      
      trainGain.gain.setValueAtTime(0, startTime);
      trainGain.gain.linearRampToValueAtTime(0.4, startTime + 0.1);
      trainGain.gain.setValueAtTime(0.4, startTime + 0.8);
      trainGain.gain.linearRampToValueAtTime(0, startTime + 0.9);
      
      trainGain.gain.setValueAtTime(0, startTime + 1.0);
      trainGain.gain.linearRampToValueAtTime(0.4, startTime + 1.1);
      trainGain.gain.setValueAtTime(0.4, startTime + 1.8);
      trainGain.gain.linearRampToValueAtTime(0, startTime + 2.0);
      
      oscillator1.start(startTime);
      oscillator2.start(startTime);
      oscillator1.stop(startTime + 2.0);
      oscillator2.stop(startTime + 2.0);
      
      return () => {
        try { oscillator1.stop(); oscillator2.stop(); } catch {}
      };
    }
    
    default:
      return () => {};
  }
}

export default function Help() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFlashing, setIsFlashing] = useState(true);
  const settings = getUserSettings();
  const { lang, dir, t } = useTranslation();
  const isHebrew = lang === "he";
  const { getElementStyle } = useElementOverrides();
  const isDark = document.documentElement.classList.contains("dark");
  const helpIconStyle = getElementStyle("help-main-icon", isDark ? "dark" : "light");
  const audioContextRef = useRef<AudioContext | null>(null);
  const stopSoundRef = useRef<(() => void) | null>(null);
  const soundIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check for selected message from Emergency screen
  const selectedMessage = location.state?.selectedMessage as string | undefined;
  const selectedEmoji = location.state?.selectedEmoji as string | undefined;
  const selectedIconName = location.state?.selectedIconName as string | undefined;
  const selectedIconMode = location.state?.selectedIconMode as "emoji" | "lucide" | undefined;
  const SelectedHelpIcon = selectedIconMode === "lucide" && selectedIconName
    ? icons[selectedIconName as keyof typeof icons]
    : undefined;
  
  const helpMessage = selectedMessage || (settings.gender === "female" ? t("iNeedHelpFemale") : t("iNeedHelpMale"));
  const closeText = t("close");
  const whatHappenedText = t("whatHappened");

  const helpAlertType = settings.helpAlertType || "voice";

  const handleClose = useCallback(() => {
    stopSpeaking();
    if (stopSoundRef.current) stopSoundRef.current();
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }
    // Always navigate to home when closing - even after selecting from Emergency
    navigate('/');
  }, [navigate]);

  const handleOpenEmergencyBoard = useCallback(() => {
    stopSpeaking();
    if (stopSoundRef.current) stopSoundRef.current();
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    navigate("/emergency", { state: { fromHelp: true } });
  }, [navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Send push notification to caregivers when help is triggered
  const sendPushAlert = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const user = session.user;
      if (user) {
        await supabase.functions.invoke('send-push-alert', {
          body: {
            patientId: user.id,
            alertType: 'help',
          },
        });
      }
    } catch (error) {
      // Silently handle - push notifications are optional
    }
  }, []);

  // Play alert based on type
  const playAlert = useCallback(() => {
    if (settings.isMuted) return;
    
    if (helpAlertType === "voice") {
      speak({
        text: helpMessage,
        gender: settings.voiceGender || "female",
        neutral: true,
      });
    } else {
      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
        audioContextRef.current = new AudioContext();
      }
      
      if (stopSoundRef.current) {
        stopSoundRef.current();
      }
      
      stopSoundRef.current = createAlertSound(helpAlertType, audioContextRef.current);
    }
  }, [helpAlertType, helpMessage, settings.voiceGender, settings.isMuted, isHebrew]);

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlashing((prev) => !prev);
    }, 500);

    // Initial play
    playAlert();
    
    // Send push notification to caregivers
    sendPushAlert();

    // Repeat every 4 seconds
    soundIntervalRef.current = setInterval(playAlert, 4000);

    return () => {
      clearInterval(interval);
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
      stopSpeaking();
      if (stopSoundRef.current) {
        stopSoundRef.current();
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    };
  }, [playAlert, sendPushAlert]);

  return (
    <MainLayout onBack={handleBack}>
      <div className="flex-1 flex items-center justify-center bg-background p-2 sm:p-4 overflow-hidden">
        <div
          className="w-full h-full flex flex-col items-center justify-center rounded-3xl text-center transition-all duration-300 p-4 sm:p-8"
          style={{
            maxHeight: 'calc(100vh - 8rem)',
            backgroundColor: isFlashing ? '#FBDDCF' : '#F5D0BF',
          }}
        >
          {/* Icon/Emoji - responsive sizing */}
          <div className="flex-shrink-0" data-design-id="help-main-icon">
            {selectedEmoji || SelectedHelpIcon ? (
              <div className="mx-auto rounded-2xl bg-card flex items-center justify-center shadow-md border border-border"
                style={{
                  width: 'clamp(5rem, 14vh, 8rem)',
                  height: 'clamp(5rem, 14vh, 8rem)',
                }}
              >
                {SelectedHelpIcon ? (
                  <SelectedHelpIcon
                    className="text-foreground"
                    style={{ width: 'clamp(3rem,8vh,5rem)', height: 'clamp(3rem,8vh,5rem)' }}
                  />
                ) : selectedEmoji?.startsWith("data:image") ? (
                  <img
                    src={selectedEmoji}
                    alt=""
                    className="object-contain rounded-lg"
                    style={{ width: 'clamp(3rem,8vh,5rem)', height: 'clamp(3rem,8vh,5rem)' }}
                  />
                ) : (
                  <span className="text-[clamp(3rem,8vh,5rem)]">{selectedEmoji}</span>
                )}
              </div>
            ) : (
              <HeartHandshake
                className={cn(
                  !helpIconStyle.iconColor && "text-accent",
                  isFlashing && "animate-pulse"
                )}
                style={{
                  width: 'clamp(4rem, 12vh, 7rem)',
                  height: 'clamp(4rem, 12vh, 7rem)',
                  ...(helpIconStyle.iconColor ? { color: helpIconStyle.iconColor } : {}),
                }}
              />
            )}
          </div>

          {/* Message - responsive text */}
          {selectedMessage ? (
            <div className="my-4 sm:my-6 mx-auto px-8 py-4 rounded-2xl bg-card shadow-md border border-border max-w-md">
              <h1 
                className="font-bold text-foreground"
                style={{
                  fontSize: 'calc(clamp(1.5em, 5vh, 3em) * var(--font-scale, 1))',
                }}
              >
                {helpMessage}
              </h1>
            </div>
          ) : (
            <h1 
              className="font-bold text-foreground my-4 sm:my-6 help-title-text"
              style={{
                fontSize: 'calc(clamp(1.5em, 5vh, 3em) * var(--font-scale, 1))',
              }}
            >
              {helpMessage}
            </h1>
          )}

          {/* Buttons container - responsive sizing */}
          <div 
            className="flex flex-row gap-4 sm:gap-8 justify-center flex-shrink-0"
            style={{
              marginTop: 'clamp(1rem, 3vh, 2rem)',
            }}
          >
            {/* In RTL: first button appears on RIGHT, second on LEFT */}
            {!selectedMessage && (
              <button
                onClick={handleOpenEmergencyBoard}
                className="flex flex-col items-center justify-center gap-3 bg-primary text-primary-foreground rounded-2xl sm:rounded-3xl font-bold hover:scale-105 transition-all aspect-square shadow-md overflow-hidden px-2 text-center"
                style={{
                  width: 'clamp(130px, 22vh, 220px)',
                  height: 'clamp(130px, 22vh, 220px)',
                }}
                data-scan-item
              >
                <HelpCircle 
                  style={{
                    width: 'clamp(2.5rem, 7vh, 5rem)',
                    height: 'clamp(2.5rem, 7vh, 5rem)',
                  }}
                />
                <span 
                  className="font-bold"
                  style={{
                    fontSize: 'calc(clamp(1em, 2.5vh, 1.5em) * var(--card-label-scale-cap, 1))',
                  }}
                >
                  {whatHappenedText}
                </span>
              </button>
            )}
            
            <button
              onClick={handleClose}
              className="flex flex-col items-center justify-center gap-3 bg-card text-foreground rounded-2xl sm:rounded-3xl font-bold hover:scale-105 transition-all aspect-square shadow-md border border-border overflow-hidden px-2 text-center"
              style={{
                width: 'clamp(130px, 22vh, 220px)',
                height: 'clamp(130px, 22vh, 220px)',
              }}
              data-scan-item
            >
              <X 
                className="text-destructive"
                style={{
                  width: 'clamp(2.5rem, 7vh, 5rem)',
                  height: 'clamp(2.5rem, 7vh, 5rem)',
                }}
              />
              <span 
                className="font-bold"
                style={{
                  fontSize: 'calc(clamp(1em, 2.5vh, 1.5em) * var(--card-label-scale-cap, 1))',
                }}
              >
                {closeText}
              </span>
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}